import { type FC, type ReactNode } from "react";

import { formatCurrency } from "@/shared/utils/formatters";
import { groupDebtsByType } from "@/shared/utils/estateMath";
import { findLinkedSecuredDebts } from "@/pages/postloss/estate-inventory/utils/estateItemUtils";
import { cn } from "@/shared/utils/core";

import type { EstateItem } from "@/shared/types/estateTypes";

interface LinkedDebtSummaryProps {
  item: EstateItem;
  allItems: EstateItem[];
  defaultRow: ReactNode;
}

export const LinkedDebtSummary: FC<LinkedDebtSummaryProps> = ({ item, allItems, defaultRow }) => {
  const linkedDebts = findLinkedSecuredDebts(item, allItems);
  if (linkedDebts.length === 0) return defaultRow;

  const groupedDebts = Object.entries(groupDebtsByType(linkedDebts)).map(([type, debtItems]) => ({
    type,
    balance: debtItems.reduce((sum, d) => sum + d.balance, 0),
  }));
  const totalDebt = linkedDebts.reduce((sum, d) => sum + d.balance, 0);
  const netValue = item.balance - totalDebt;

  return (
    <div
      key={item.id}
      className={cn(
        "overflow-hidden rounded-lg border [&>div]:border-0 [&>div]:rounded-none",
        item.extraction_status === "pending" ? "border-brand" : "border-primary",
      )}
    >
      {defaultRow}
      {groupedDebts.map(({ type, balance }) => (
        <div
          key={type}
          className="flex items-center justify-between border-t border-primary px-4 py-3"
        >
          <p className="text-sm">
            <span className="font-medium">{type}</span>
            <span className="text-secondary"> &middot; Remaining balance</span>
          </p>
          <p className="text-sm font-medium">-{formatCurrency(balance)}</p>
        </div>
      ))}
      <div className="flex items-center justify-between border-t border-primary px-4 py-3">
        <p className="text-sm font-medium">Net to estate</p>
        <p className="text-sm font-medium">{formatCurrency(netValue)}</p>
      </div>
    </div>
  );
};
